import { Box, Button, CircularProgress, Divider, Grid } from "@mui/material";
import {
  GridColDef,
  GridValueFormatterParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import Layout from "components/Layout";
import {
  useListUserAlertsQuery,
  useUpdateUserAlertMutation,
  useListStrategiesQuery,
} from "features/trading/accounts";
import { useAuth, useFactsetStreaming } from "features/store";
import dayjs from "dayjs";
import { ServerDataGrid, usePagination } from "components/ui/ServerDataGrid";
import { useNavigate } from "react-router-dom";
import { useCallback, useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import StockPicker from "components/holdings/StockPicker";
import { alertPageStyles } from "../styles/AlertsPage.styles";
import { FDS_SubscriptionTypes } from "constants/index";
import { useFDSWebSocket } from "../hooks/useFDSWebSocket";
import { extractStockSymbols, extractOptionSymbols } from "../utils/fdsUtils";
import { AlertDescription } from "../components/alerts/AlertDescription";
import { useMarkUserAlertsSeenMutation } from "features/trading/accounts";
import { RedirectionModal } from "components/holdings/Alert/RedirectionModal";

const AlertTypeEnum = {
  trade_signal: "Trade Signal",
  position_mismatch: "Position Mismatch",
};

const wrapWithLayout = (child: JSX.Element) => {
  return (
    <Layout title="Alerts" content="Alerts page">
      {child}
    </Layout>
  );
};

export const AlertsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [pagination, setPagination] = usePagination(10);
  const [alertType, setAlertType] = useState(AlertTypeEnum.trade_signal);
  const [isSeen, setIsSeen] = useState<boolean | undefined>(undefined);
  const [selectedStrategyId, setSelectedStrategyId] = useState<
    number | undefined
  >(undefined);
  const [selectedSymbol, setSelectedSymbol] = useState<string | undefined>(
    undefined,
  );
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [isUserAction, setIsUserAction] = useState(false);
  const [redirectionModalOpen, setRedirectionModalOpen] = useState(false);
  const [symbol, setSymbol] = useState<string | undefined>(undefined);
  const [callOrPut, setCallOrPut] = useState<boolean | undefined>(undefined);
  const [alertId, setAlertId] = useState<number | undefined>(undefined);
  const {
    subscriptions,
    dataCache: {
      [FDS_SubscriptionTypes.HOMEPAGE_STOCK]: FDS_stockData,
      [FDS_SubscriptionTypes.HOMEPAGE_OPTION]: FDS_optionData,
    },
  } = useFactsetStreaming();

  const { data, isLoading, isFetching } = useListUserAlertsQuery(
    {
      pagination,
      filters: {
        user: user?.id,
        seen: isSeen ?? undefined,
        alert__type: alertType,
        alert__symbol: selectedSymbol ?? undefined,
      },
    },
    {
      skip: !isAuthenticated && !user,
      skipPollingIfUnfocused: true,
      pollingInterval: 30_000,
    },
  );

  const [updateUserAlert] = useUpdateUserAlertMutation();
  const [markUserAlertsSeen, { isLoading: isMarking }] =
    useMarkUserAlertsSeenMutation();

  // Get all strategies to find the selected one
  const { data: strategiesData } = useListStrategiesQuery({
    filters: {
      detailed: true,
      exclude_non_group: true,
    },
    pagination: { page: 0, pageSize: 1000 },
  });

  // Find the selected strategy
  const selectedStrategy = useMemo(() => {
    if (!selectedStrategyId || !strategiesData?.results) return null;
    return strategiesData.results.find(
      (strategy) => strategy.id === selectedStrategyId,
    );
  }, [selectedStrategyId, strategiesData]);

  // Extract symbols from trade_signal alerts for FDS streaming
  const { stockSubKey, optionSubKey } = useMemo(() => {
    if (
      !data ||
      data.results.length === 0 ||
      alertType !== AlertTypeEnum.trade_signal
    ) {
      return { stockSubKey: null, optionSubKey: null };
    }

    const tradeSignalAlerts = data.results.filter(
      (alert) => alert.type === "Trade Signal",
    );
    const stockSymbols = extractStockSymbols(tradeSignalAlerts);
    const optionSymbols = extractOptionSymbols(tradeSignalAlerts);

    return {
      stockSubKey:
        stockSymbols.length > 0
          ? FDS_SubscriptionTypes.HOMEPAGE_STOCK + ":" + stockSymbols.join(",")
          : null,
      optionSubKey:
        optionSymbols.length > 0
          ? FDS_SubscriptionTypes.HOMEPAGE_OPTION +
            ":" +
            optionSymbols.join(",")
          : null,
    };
  }, [data, alertType]);

  // Use custom WebSocket hook
  useFDSWebSocket(
    stockSubKey,
    optionSubKey,
    subscriptions,
    undefined,
    dispatch,
  );

  // Update selected symbol when strategy changes
  useEffect(() => {
    if (selectedStrategy) {
      setSelectedSymbol(selectedStrategy.name);
    } else {
      setSelectedSymbol(undefined);
    }
  }, [selectedStrategy]);

  // Reset user action flag when fetch completes
  useEffect(() => {
    if (!isFetching && isUserAction) {
      setIsUserAction(false);
    }
  }, [isFetching, isUserAction]);

  const markAsSeen = useCallback(
    (id: number) => {
      updateUserAlert({ id, seen: true });
    },
    [updateUserAlert],
  );

  const onCheckClick = useCallback((id: number) => {
    setAlertId(id);
    setRedirectionModalOpen(true);
  }, []);

  const columns: GridColDef[] = [
    {
      field: "created_on",
      headerName: "Time handled",
      valueFormatter: ({ value }: GridValueFormatterParams) =>
        value
          ? dayjs
              .utc(value)
              .tz("America/New_York")
              .format("MM/DD/YYYY hh:mm:ss A")
          : "N/A",
      minWidth: 200,
      cellClassName: "font-mono text-sm",
    },
    {
      field: "time_sent",
      headerName: "Time Sent",
      valueFormatter: ({ value }: GridValueFormatterParams) =>
        value ? dayjs(value).format("MM/DD/YYYY hh:mm:ss A") : "N/A",
      minWidth: 200,
      cellClassName: "font-mono text-sm",
    },
    {
      field: "type",
      headerName: "Type",
      minWidth: 150,
      renderCell: ({ value }) => (
        <Box sx={alertPageStyles.typeBadge(value === "Trade Signal")}>
          {value}
        </Box>
      ),
    },
    {
      field: "order_id",
      headerName: "UID",
      minWidth: 150,
      renderCell: ({ value }) => (
        <Box sx={alertPageStyles.symbolBadge}>{value || "N/A"}</Box>
      ),
    },
    {
      field: "payload",
      headerName: "Description",
      renderCell: ({ row }) => (
        <AlertDescription
          {...row}
          FDS_stockData={FDS_stockData}
          FDS_optionData={FDS_optionData}
        />
      ),
      minWidth: 100,
      flex: 2,
    },
    {
      field: "action",
      headerName: "Action Recommended",
      minWidth: 100,
      flex: 2,
      renderCell: (params) => {
        const {
          row: { id, seen, payload },
        } = params;
        return (
          <Box sx={alertPageStyles.actionButtonsContainer}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={() => onCheckClick(id)}
              sx={alertPageStyles.checkButton}
            >
              Check
            </Button>
            <Button
              variant={seen ? "outlined" : "contained"}
              size="small"
              color={seen ? "inherit" : "secondary"}
              onClick={() => markAsSeen(id)}
              disabled={seen}
              sx={alertPageStyles.markAsSeenButton(seen)}
            >
              {seen ? "Seen" : "Mark as seen"}
            </Button>
          </Box>
        );
      },
    },
  ];

  if (isLoading || (isFetching && isUserAction) || !data) {
    return wrapWithLayout(<CircularProgress />);
  }

  const handleAlertTypeChange = (type: string) => {
    setAlertType(type);
    setSelectedSymbol(undefined);
    setSelectedStrategyId(undefined);
    setIsUserAction(true);
  };

  const handleIsSeenChange = (isSeen: boolean | undefined) => {
    setIsSeen(isSeen);
    setIsUserAction(true);
  };

  const handleStrategySearchChange = (value: number | undefined) => {
    setSelectedStrategyId(value);
    setIsUserAction(true);
  };

  const handlePaginationChange = (newPagination: any) => {
    setPagination(newPagination);
    setIsUserAction(true);
  };

  const handleMarkFilteredAsSeen = async () => {
    setIsUserAction(true);
    if (selectedIds.length > 0) {
      await markUserAlertsSeen({ ids: selectedIds as number[] });
      setSelectedIds([]);
      return;
    }
    await markUserAlertsSeen({
      filters: {
        user: user?.id,
        seen: false,
        alert__type: alertType,
        alert__symbol: selectedSymbol ?? undefined,
      },
    });
  };

  return wrapWithLayout(
    <Box>
      <Grid container alignItems="center" columnGap={1} rowGap={1}>
        <Grid item xs="auto">
          <Button
            variant={`${isSeen === undefined ? "contained" : "outlined"}`}
            size="small"
            sx={{ ml: 1 }}
            onClick={() => handleIsSeenChange(undefined)}
          >
            All
          </Button>
          <Button
            variant={`${isSeen === false ? "contained" : "outlined"}`}
            size="small"
            sx={{ ml: 1 }}
            onClick={() => handleIsSeenChange(false)}
          >
            Unread
          </Button>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item xs="auto">
          <Button
            variant={`${alertType === AlertTypeEnum.trade_signal ? "contained" : "outlined"}`}
            size="small"
            onClick={() => handleAlertTypeChange(AlertTypeEnum.trade_signal)}
          >
            Trade Signal
          </Button>
          <Button
            variant={`${alertType === AlertTypeEnum.position_mismatch ? "contained" : "outlined"}`}
            size="small"
            sx={{ ml: 1 }}
            onClick={() =>
              handleAlertTypeChange(AlertTypeEnum.position_mismatch)
            }
          >
            Position Mismatch
          </Button>
        </Grid>
        {alertType === AlertTypeEnum.trade_signal ? (
          <>
            <Divider orientation="vertical" flexItem />
            <Grid item xs="auto">
              <StockPicker onChange={handleStrategySearchChange} />
            </Grid>
          </>
        ) : (
          <></>
        )}
        <Divider orientation="vertical" flexItem />
        <Grid item xs="auto">
          <Button
            variant="contained"
            size="small"
            color="inherit"
            onClick={handleMarkFilteredAsSeen}
            disabled={isMarking}
            sx={{ ml: 1 }}
          >
            {isMarking
              ? "Marking..."
              : selectedIds.length > 0
                ? "Mark selected as seen"
                : "Mark filtered as seen"}
          </Button>
        </Grid>
      </Grid>

      <ServerDataGrid
        columns={columns}
        rows={data?.results}
        rowCount={data?.count}
        density="compact"
        getRowHeight={() => "auto"}
        paginationModel={pagination}
        onPaginationModelChange={handlePaginationChange}
        checkboxSelection
        rowSelectionModel={selectedIds}
        onRowSelectionModelChange={(model) => setSelectedIds(model)}
        sx={{
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #f0f0f0",
            padding: "8px 16px",
          },
          "& .MuiDataGrid-row": {
            "&:hover": {
              backgroundColor: "#f8f9fa",
            },
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f8f9fa",
            borderBottom: "2px solid #e9ecef",
          },
        }}
      />
      <RedirectionModal
        open={redirectionModalOpen}
        onClose={() => setRedirectionModalOpen(false)}
        alertId={alertId}
      />
    </Box>,
  );
};
